import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Clock, ChevronLeft, ChevronRight, Users, Ban, Shield, FileText, Truck, Headphones, CreditCard, Star } from "lucide-react";
import BookingModal from "@/components/BookingModal";
import { bookableServices } from "@/data/serviceCatalog";
import { fetchApi } from "@/lib/apiBase";
import { DEFAULT_PUBLIC_HOURS_ROWS, getPublicHoursRows } from "@/lib/publicHours";

// Hero image (local file first, fallback keeps app usable if file is missing)
const STYLIST_HERO_IMAGE = "/images/home-hero.jpg?v=highres";

// Why choose us features
const features = [
  {
    icon: Truck,
    title: "Flexible Booking",
    description: "Easy online scheduling with convenient time slots to fit your lifestyle."
  },
  {
    icon: Headphones,
    title: "Customer Service",
    description: "I'm available Monday to Friday to answer your questions."
  },
  {
    icon: CreditCard,
    title: "Secure Payment",
    description: "Your payment information is processed securely."
  },
  {
    icon: Star,
    title: "Top Quality",
    description: "Premium products and techniques for lasting, beautiful results."
  },
];

// Policy buttons data
const policies = [
  { title: "Arrival Policy & Guest Policy", icon: Clock },
  { title: "Family Policy", icon: Users },
  { title: "Cancellations & No-Shows", icon: Ban },
  { title: "Security & Privacy Policy", icon: Shield },
];

// Marquee text
const marqueeText = "GREAT HAIR SPEAKS LOUDER THAN WORDS";

// Optional local overrides. Add files in /public/images/services to replace service images.
const featuredServiceImageOverrides = {
  "wig-install": "/images/services/wig-install.jpg",
  "traditional-sew-in": "/images/services/traditional-sew-in.jpg",
  quickweave: "/images/services/quickweave.jpg",
};

const featuredServiceOrder = ["wig-install", "traditional-sew-in", "quickweave"];
const featuredServiceFallbacks = {
  "wig-install": {
    id: "wig-install",
    name: "Wig Install",
    description: "Professional wig installation with secure, natural-looking placement for a flawless finish.",
    price: "Starting at $120",
    duration: "2 hr",
    image: "/images/services/wig-install.jpg",
  },
  "traditional-sew-in": {
    id: "traditional-sew-in",
    name: "Traditional Sew-in",
    description: "Classic sew-in weave technique for versatile styling and long-lasting wear.",
    price: "Starting at $180",
    duration: "3 hr",
    image: "/images/services/traditional-sew-in.jpg",
  },
  quickweave: {
    id: "quickweave",
    name: "Quickweave",
    description: "Fast, sleek quickweave install with a polished finish and natural blend.",
    price: "Starting at $130",
    duration: "2 hr",
    image: "/images/services/quickweave.jpg",
  },
};

const homeServiceCategoryMap = {
  "wig-install": "wig-install-packages",
  "traditional-sew-in": "sew-in-packages",
  quickweave: "quickweaves-packages",
};

const homeServiceDefaultPackageMap = {
  "wig-install": "wig-install-packages-basic-frontal-install-package",
  "traditional-sew-in": "sew-in-packages-basic-traditional-sew-in-package",
  quickweave: "quickweaves-packages-basic-quickweave-package",
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [hours, setHours] = useState(DEFAULT_PUBLIC_HOURS_ROWS);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const revealRefs = useRef([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetchApi("/services");
        const data = await res.json();
        setServices(data);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    let active = true;

    getPublicHoursRows().then((rows) => {
      if (active) {
        setHours(rows);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [services]);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const openBooking = (serviceId = null, categoryId = null) => {
    setSelectedServiceId(serviceId);
    setSelectedCategoryId(categoryId);
    setIsBookingOpen(true);
  };

  const featuredServices = featuredServiceOrder
    .map((id) => services.find((service) => service.id === id) || featuredServiceFallbacks[id])
    .filter(Boolean);

  const resolveHomeServicePackageId = (serviceId) => {
    const preferred = homeServiceDefaultPackageMap[serviceId];
    if (preferred && bookableServices.some((s) => s.id === preferred || s.service_id === preferred)) {
      return preferred;
    }
    const categoryId = homeServiceCategoryMap[serviceId];
    const firstInCategory = bookableServices.find((s) => s.categoryId === categoryId);
    return firstInCategory?.id || null;
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(1, services.length - 2));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.max(1, services.length - 2)) % Math.max(1, services.length - 2));
  };

  return (
    <div className="bg-[#F5F5F5]">
      {/* Hero Section - Full Screen Background Image including behind navbar */}
      <section
        data-testid="hero-section"
        className="hero-background relative h-screen w-full overflow-hidden"
        style={{ backgroundImage: `url(${STYLIST_HERO_IMAGE})` }}
      >
        <div className="hero-stage absolute inset-0" />
        <div className="hero-vignette absolute inset-0 pointer-events-none" />
        <div className="hero-light-sweep absolute inset-0 pointer-events-none" />
      </section>

      {/* Marquee Banner */}
      <section className="bg-[#111111] py-3 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...Array(6)].map((_, i) => (
              <span key={i} className="marquee-text">
                {marqueeText} <span className="mx-8">★</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal text-center mb-12">
            <h2 className="script-heading text-2xl md:text-3xl">
              Why Book with DadaDeLuxe
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                ref={addRevealRef}
                className="reveal text-center"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-[#111111] rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-white" size={20} />
                </div>
                <h3 className="font-medium text-sm mb-2">{feature.title}</h3>
                <p className="text-xs text-[#666] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        data-testid="services-preview-section"
        className="py-16 lg:py-24 bg-[#F5F5F5]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal text-center mb-12">
            <p className="script-heading text-2xl md:text-3xl">Select a Service to Book Appointment</p>
          </div>

          {/* Services Grid */}
          <div className="relative">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredServices.map((service, index) => (
                <div
                  key={service.id}
                  ref={addRevealRef}
                  data-testid={`service-card-${service.id}`}
                  className="reveal service-card cursor-pointer group"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() =>
                    openBooking(
                      resolveHomeServicePackageId(service.id),
                      homeServiceCategoryMap[service.id] || null
                    )
                  }
                >
                  <div className="service-card-image">
                    <img
                      src={featuredServiceImageOverrides[service.id] || service.image}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <div className="service-card-overlay">
                      <h3 className="service-card-title">{service.name}</h3>
                      <p className="text-sm text-white/80 line-clamp-2">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="service-card-info">
                    <span className="font-medium">{service.price}</span>
                    <span className="flex items-center gap-1 text-sm text-[#888]">
                      <Clock size={14} /> {service.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* View All Services */}
            <div className="text-center mt-8">
              <Link
                to="/services"
                className="inline-block btn-outline"
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Buttons */}
      <section
        data-testid="policy-section"
        className="py-12 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal flex flex-wrap justify-center gap-3">
            {policies.map((policy, index) => (
              <Link
                key={index}
                to="/policies"
                data-testid={`policy-card-${index}`}
                className="policy-btn"
              >
                <span className="policy-btn-icon">
                  <policy.icon size={14} />
                </span>
                <span>{policy.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        data-testid="about-section"
        className="py-16 lg:py-24 bg-[#F5F5F5]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal glass-card p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <div className="flex justify-center md:justify-start">
                <div className="w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden shadow-lg">
                  <img
                    src={STYLIST_HERO_IMAGE}
                    alt="Adama"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 768px) 80vw, 288px"
                  />
                </div>
              </div>
              
              {/* Text */}
              <div className="text-center md:text-left">
                <h2 className="script-heading text-2xl md:text-3xl mb-4">
                  Hey! I'm Adama
                </h2>
                <p className="text-[#666] leading-relaxed mb-6">
                  I'm a 21-year-old hairstylist with over five years of experience 
                  specializing in wigs, sew-ins, and quick weaves. Based in West 
                  Philadelphia, I provide a comfortable, elevated space for my clients 
                  at The Luxe Loft.
                </p>
                <Link
                  to="/about"
                  data-testid="about-link"
                  className="text-[#111] underline underline-offset-4 hover:text-[#666] transition-colors"
                >
                  Learn more about me
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hours of Operation */}
      <section
        data-testid="hours-section"
        className="py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal max-w-md mx-auto">
            <div className="hours-table">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-center font-medium">Hours of Operation</h3>
              </div>
              {hours.map((item, index) => (
                <div key={index} className="hours-row">
                  <span>{item.day}</span>
                  <span className="text-white/80">{item.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-16 lg:py-24 bg-[#F5F5F5]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={addRevealRef} className="reveal text-center">
            <h2 className="script-heading text-3xl md:text-4xl mb-4">
              Ready for Your Transformation?
            </h2>
            <p className="text-[#666] mb-8 max-w-lg mx-auto">
              Book your appointment today and experience luxury hair styling.
            </p>
            <button
              data-testid="cta-book-btn"
              onClick={() => openBooking()}
              className="btn-black"
            >
              Book Your Appointment
            </button>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        preSelectedService={selectedServiceId}
        preSelectedCategoryId={selectedCategoryId}
      />
    </div>
  );
}
